import '@/core/src/operation/localeDayjs';
import { QUOTE_SYS_PROP } from '@/core/src/shared/constant';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ApiResponse } from '../@types/ApiResponse';
import { CURRENCIES, EXTERNAL_OWNER, NODE_TYPE } from '../constants';
import { execCalcFormula, propIsNull } from '../operation';
import { getMatchingOriginProp } from './prop';
import { toArray } from './util';
import { isEmpty } from 'ramda';
dayjs.extend(utc);

export const getNodeToppingViews = (node: PiNode) => {
  if (node.prop._sys_topping_views) {
    const toppingViews = Object.keys(node.prop._sys_topping_views)?.sort((a, b) => {
      if (node.prop._sys_topping_views)
        return node.prop._sys_topping_views[a] - node.prop._sys_topping_views[b];
      return 1;
    });
    return toppingViews;
  }

  return [];
};

/**
 * 获取过滤器节点的源节点id
 */
export const getVirtualNodeOriginId = (nodeManager: PiNodeManager | null, nodeId: string) => {
  let _originId = nodeId;

  if (nodeManager) {
    const node = nodeManager.findChildren(nodeId);
    if (node) {
      if (node.nodeType === NODE_TYPE.FILTERNODE_PATH) {
        const [filterId, ..._] = nodeId.split('>');
        _originId = filterId;
      }
      if (node.nodeType === NODE_TYPE.FILTERNODE_LEAF) {
        const nodeSplits = nodeId.split('>');
        _originId = nodeSplits[0];
      }
    } else {
      // 节点不存在 可能未展开 通过>判断
      if (~nodeId.indexOf('>')) {
        // 返回源节点
        const nodeSplits = nodeId.split('>');
        _originId = nodeSplits[0];
      }
    }
  }

  return [_originId];
};

type UserIdType = string;
type TempValueDisplayArgType = {
  propConfig: ApiResponse.CurrentUser.TemplateProp | '-3' | null | undefined;
  propValue: any;
  userMap: Record<UserIdType, ApiResponse.OrgUser.OrgUserItem>;
  tempMap: Record<string, ApiResponse.CurrentUser.TemplateInfo>;
  departmentMap?: Record<string, ApiResponse.OrgGroups.OrgDepartmentNodeInfo>;
  sysCascade?: PiNode['prop']['_sys_cascade'];
  propIndex?: number;
};

/** 获取属性的 tostring 显示 */
export const getPropHandledValue = (
  prop: ApiResponse.CurrentUser.TemplateProp | '-3' | null,
  propIndex: number,
  value: any,
  userMap: Record<UserIdType, ApiResponse.OrgUser.OrgUserItem>,
  sysCascade: PiNode['prop']['_sys_cascade'],
) => {
  // 如果没有值
  if (propIsNull(value) || !prop) return null;

  // 标题
  if (prop === '-3') {
    return Array.isArray(value) ? value.map((v) => displayTitle(v)) : [displayTitle(value)];
  }

  // 选值 文本属性(多条情况后端拍平了)
  if (['str', 'text', 'tag', 'enum'].includes(prop.type)) {
    return Array.isArray(value) ? value : [value];
  }

  // date属性
  if (prop.type === 'date' || prop.type === 'datetime') {
    const f = prop.dateFormat || (prop.type === 'date' ? 'YYYY/MM/DD' : 'YYYY/MM/DD HH:mm:ss');
    return Array.isArray(value)
      ? value.map((v) => (f === 'HH:mm' ? dayjs(Number(v)).utc() : dayjs(Number(v))).format(f))
      : [(f === 'HH:mm' ? dayjs(Number(value)).utc() : dayjs(Number(value))).format(f)];
  }

  // user
  if (prop.type === 'user') {
    const users = Array.isArray(value) ? value : [value];
    return users.map((v) => (v === EXTERNAL_OWNER ? '待认领' : fetchUserName(v, userMap)));
  }

  // number
  if (prop.type === 'number') {
    const numbers = Array.isArray(value) ? value : [value];

    const { unitPosition, unit, numericalFormat, precision, locale = false } = prop.number || {};
    return numbers.map((n) => {
      const pre = unitPosition && unit ? unit : '';
      const v = (numericalFormat ? Number(n) * 100 : Number(n)).toFixed(precision || 0);
      const sign = numericalFormat ? '%' : '';
      const suf = !unitPosition && unit ? unit : '';

      const numberStr = locale ? Number(v).toLocaleString('en-US') : v;

      return pre + numberStr + sign + suf;
    });
  }

  // positioning
  if (prop.type === 'positioning') {
    return Array.isArray(value) ? value : [value];
  }

  // cascade
  if (prop.type === 'cascade') {
    const cascadeValue = sysCascade?.[propIndex]; // 后端在当前node也移过来了 _sys_cascade
    if (!cascadeValue) return null;

    return cascadeValue.map((c) => {
      if (typeof c === 'string') return c;

      // 只展示最后一级
      if (prop.hideRoutes) return c[c.length - 1];

      return c.join('/');
    });
  }

  // formula
  if (prop.type === 'formula') {
    const formulas = Array.isArray(value) ? value : [value];

    const { unitPosition, unit, numericalFormat, precision } = prop.number || {};

    return formulas.map((v) => {
      // 文本
      if (String(Number(v)) !== String(v)) return v;

      const pre = unitPosition && unit ? unit : '';
      const n = (numericalFormat ? Number(v) * 100 : Number(v)).toFixed(precision || 0);
      const sign = numericalFormat ? '%' : '';
      const suf = !unitPosition && unit ? unit : '';

      return pre + n + sign + suf;
    });
  }
};

/** 获取属性的 tostring 显示 */
export const tempValueDisplay = (props: TempValueDisplayArgType): string | number => {
  const { propConfig, propValue, propIndex, sysCascade, userMap, tempMap, departmentMap } = props;
  if (!propConfig || propIsNull(propValue)) return '';

  // 标题
  if (propConfig === '-3') {
    return Array.isArray(propValue)
      ? propValue.map((x) => displayTitle(x)).join(' , ')
      : displayTitle(propValue);
  }

  let switchType = propConfig.type;
  let switchValue = propValue;

  // 引用属性 通过引用源显示
  if (propConfig.type === 'quote') {
    // 兼容老的引用（前端逻辑）
    const isOldQuote = !propConfig.formula || !Object.keys(propConfig.formula).length;
    if (isOldQuote) {
      switchType = propValue.props?.[0]?.type;
      switchValue = propValue.props?.[0]?.value || '';
    } else {
      const originProp = getMatchingOriginProp(propConfig, tempMap, []);
      return tempValueDisplay({
        ...props,
        // FIXME: type error @yuan7
        propConfig: originProp as ApiResponse.CurrentUser.TemplateProp,
      });
    }
  }

  // 匹配/引用统计
  if (propConfig.conditionMatching) {
    if (propConfig.matchingType > 0) {
      if (!Number.isFinite(Number(propValue))) {
        // 文本，选值属性可以继续使用自身属性值
        if (!['text', 'enum', 'tag'].includes(propConfig.type)) {
          return '数据异常';
        }
      }
      // 计数统计直接返回，不考虑精度
      else if (propConfig.matchingType === 5) return Number(propValue);
      else if (Number.isFinite(Number(propValue))) {
        // 处理精度
        const precision =
          propConfig.number?.precision ??
          propConfig.extend?.precision ??
          propConfig.matchingPrecision ??
          0;
        return Number(propValue).toFixed(precision);
      }
    } else if (propConfig.matchingType === 0 && propConfig.type === 'text') {
      //TODO 文本属性 匹配 {引用属性（引用数字-原值）} {引用属性（引用数字公式-原值）} 需要特殊处理 是否是只要匹配引用属性都需要特殊处理？
      const matching = propConfig.matchingProp?.[0];
      if (matching && !(matching in QUOTE_SYS_PROP)) {
        const [matchTempId, matchPropIndex] = matching.split('-');
        const quoteProp = tempMap[matchTempId]?.prop?.[Number(matchPropIndex)];
        if (quoteProp?.type === 'quote') {
          return tempValueDisplay({
            ...props,
            propConfig: quoteProp,
          });
        }
      }
    }
  }
  let display: number | string = '';

  switch (switchType) {
    case 'str':
    case 'text':
    case 'tag':
    case 'enum':
      display = Array.isArray(switchValue) ? switchValue.join(';') : switchValue;
      break;
    case 'date':
    case 'datetime':
      const _datetimeValue = Array.isArray(switchValue)
        ? Number(switchValue[0])
        : Number(switchValue);
      const dateFormat =
        propConfig.dateFormat || (switchType === 'date' ? 'YYYY/MM/DD' : 'YYYY/MM/DD HH:mm:ss');
      display = (
        dateFormat === 'HH:mm' ? dayjs(_datetimeValue).utc() : dayjs(_datetimeValue)
      ).format(dateFormat);
      break;
    case 'user':
      display = (Array.isArray(switchValue) ? switchValue : [switchValue])
        .map((x, index) => (x === EXTERNAL_OWNER ? '待认领' : fetchUserName(x, userMap)))
        .join(';');
      break;
    case 'number':
      display = (Array.isArray(switchValue) ? switchValue : [switchValue])
        .map((sv: string | number) => {
          let numberValue = (
            propConfig.number?.numericalFormat ? Number(sv) * 100 : Number(sv)
          ).toFixed(propConfig.number?.precision || 0);

          if (numberValue.indexOf('-0') === 0 && parseFloat(numberValue) === 0) {
            numberValue = numberValue.replace('-', '');
          }

          const numberStr = propConfig.number?.locale
            ? Number(numberValue).toLocaleString('en-US', {
                minimumFractionDigits: propConfig.number?.precision || undefined,
              })
            : numberValue;

          return (
            propConfig.number?.unitPosition && propConfig.number?.unit ? propConfig.number.unit : ''
          ).concat(
            numberStr,
            propConfig.number?.numericalFormat
              ? '%'
              : propConfig.number?.unit && propConfig.number?.unitPosition !== 1
              ? propConfig.number?.unit
              : '',
          );
        })
        .join(';');
      break;
    case 'currency':
      let precision = propConfig.extend?.precision || 2;
      if (propConfig.number && 'matchingPrecision' in propConfig.number) {
        precision = propConfig.number?.matchingPrecision;
      }
      const symbol = CURRENCIES.find((x) => x.type === propConfig.extend?.type)?.symbol || 'CN¥';
      display = (Array.isArray(switchValue) ? switchValue : [switchValue])
        .map((sv) => symbol.concat(Number(sv).toFixed(precision)))
        .join(';');
      break;
    case 'formula':
      display = (Array.isArray(switchValue) ? switchValue : [switchValue])
        .map((sv) => {
          if (!(propConfig.formulaFormat === 0 && Number.isFinite(Number(sv)))) return sv;

          let numberValue = (
            propConfig.number?.numericalFormat ? Number(sv) * 100 : Number(sv)
          ).toFixed(propConfig.number?.precision ?? 0);

          if (numberValue.indexOf('-0') === 0 && parseFloat(numberValue) === 0) {
            numberValue = numberValue.replace('-', '');
          }

          const numberStr = propConfig.number?.locale
            ? Number(numberValue).toLocaleString('en-US', {
                minimumFractionDigits: propConfig.number?.precision || undefined,
              })
            : numberValue;

          return (
            propConfig.number?.unitPosition && propConfig.number?.unit ? propConfig.number.unit : ''
          ).concat(
            numberStr,
            propConfig.number?.numericalFormat ? '%' : '',
            !propConfig.number?.unitPosition && propConfig.number?.unit
              ? propConfig.number.unit
              : '',
          );
        })
        .join(';');
      break;
    case 'attachment':
      // if (Array.isArray(switchValue)) {
      //   display = switchValue.join(';');
      // }
      // break;
      display = ' ';
      break;
    case 'cascade':
      if (propIndex !== undefined) {
        const cascadeValue = sysCascade?.[propIndex];
        if (cascadeValue) {
          display = cascadeValue
            .map((x) => {
              return typeof x === 'string'
                ? x
                : propConfig.hideRoutes
                ? x[x.length - 1]
                : x.join('/');
            })
            .join('、');
        } else {
          display = switchValue;
        }
      }

      break;
    case 'positioning':
    case 'address':
      // 定位属性 保存的是个数组
      display = Array.isArray(switchValue) ? switchValue[0] : switchValue;
      break;
    case 'department':
      display = toArray(switchValue)
        .filter((v) => Boolean(v) && !isEmpty(v))
        .map((v) => departmentMap?.[v]?.name || '未知部门')
        .join(';');
      break;
    default:
      display = switchValue;
      break;
  }

  if (typeof display !== 'string' && typeof switchValue !== 'number') {
    return '暂不支持';
  }

  return display;
};

const fetchUserName = (
  userId: string,
  userMap: Record<UserIdType, ApiResponse.OrgUser.OrgUserItem>,
) => {
  const user = userMap?.[userId];
  if (!user) return userId === 'CC4123F2258411EB9A4A7CD30AB8A430' ? '连接派' : '未知用户';
  return user?.nick_name;
};

const displayTitle = (title: string | null | undefined) => {
  if (typeof title === 'string' && title !== '') {
    return title;
  } else {
    return '无标题';
  }
};

/**
 * 获取节点的选项
 */
export const getNodeEnumOptions = async (props: {
  nodeId: string;
  orgId: string;
  shareToken?: string;
  formToken?: string;
  request: any;
  template: ApiResponse.CurrentUser.TemplateInfo;
  propIndexs?: number[];
  propType?: string;
  ap?: any;
}) => {
  let indexes: any[] = [];
  const enums: { [propIndex: string]: any[] } = {};

  const { nodeId, orgId, request, template, shareToken, formToken, propIndexs, propType, ap } =
    props;
  if (!nodeId || !orgId || !request || !template) return enums;

  // 获取主题类型的选值属性index
  if (template && template.prop && template.prop.length) {
    template?.prop?.forEach((p, i) => {
      if (
        p &&
        p.type &&
        (p.type === 'enum' || p.type === 'tag' || p.type === 'address') &&
        p.conditionMatching
      )
        indexes.push(i);
    });
  } else {
    Array.isArray(propIndexs) ? indexes.push(...propIndexs) : indexes.push(propIndexs);
  }

  const params: any = {
    node_id: nodeId,
    org_id: orgId,
    index: propIndexs || indexes,
    getQuoteArray: true,
    // shareToken: props.shareToken,
  };

  if (shareToken) {
    params.shareToken = shareToken;
  }

  if (formToken) {
    params.formToken = formToken;
  }
  // 获取选项
  const [err, res] = await execCalcFormula(request, params);

  if (res && res.status === 'ok' && Array.isArray(res.data)) {
    indexes.forEach((index, i) => {
      if (propIsNull(res.data[i])) return;
      const d = Array.isArray(res.data[i]) ? res.data[i] : [res.data[i]];
      enums[index] = [];
      if (
        (propType && propType === 'address') ||
        (!propType && template.prop[index].type === 'address')
      ) {
        enums[index] = d;
        return;
      }
      d.forEach((d: any) => {
        if (typeof d === 'string') {
          if (d && !enums[index].includes(d)) enums[index].push(d);
        } else if (typeof d === 'object' && d !== null && 'v' in d && !propType) {
          if (typeof d.v === 'string') {
            if (d.v && !enums[index].includes(d.v)) enums[index].push(d.v);
          } else if (Array.isArray(d.v)) {
            d.v.forEach((v: any) => {
              if (typeof v === 'string' && v && !enums[index].includes(v)) enums[index].push(v);
            });
          }
        } else if (typeof d === 'object' && d !== null && d.v && propType) {
          // const auxiliaryProp = template.prop[index].auxiliaryProp;
          const auxiliaryProp = propType ? ap : template.prop[index].auxiliaryProp;
          const aux =
            'aux' in d && auxiliaryProp
              ? {
                  label: Array.isArray(d.aux) ? d.aux.join(';') : d.aux,
                  position: auxiliaryProp?.position,
                }
              : null;
          if (typeof d.v === 'string' && !enums[index].find((x) => x.label === d.v)) {
            enums[index].push({
              label: d.v,
              value: d.v,
              aux: aux,
            });
          } else if (Array.isArray(d.v)) {
            d.v.forEach((v: any) => {
              if (typeof v === 'string' && !enums[index].find((x) => x.label === v)) {
                enums[index].push({
                  label: v,
                  value: v,
                  aux: aux,
                });
              }
            });
          }
        }
      });
    });
  }
  return enums;
};

/**
 * 获取节点的选项
 * 返回option结构
 */
export const getEnumOptions = async (props: {
  nodeId?: string;
  orgId: string;
  request: any;
  template: ApiResponse.CurrentUser.TemplateInfo;
  propIndexs?: number[];
  propType?: string;
  ap?: any;
}) => {
  const indexes: number[] = props.propIndexs || [];
  const enums: {
    [propIndex: string]: {
      label: string;
      value: string;
      aux?: {
        label: string;
        // 0显示在后，1显示在前
        position: 0 | 1;
      } | null;
    }[];
  } = {};

  const { nodeId, orgId, request, template, propType, ap } = props;
  if ((!nodeId && !props.propIndexs) || !orgId || !request || !template) return enums;

  if (!props.propIndexs?.length)
    // 获取主题类型的选值属性index
    template.prop.forEach((p, i) => {
      if (
        p &&
        p.type &&
        (p.type === 'enum' || p.type === 'tag' || p.type === 'address') &&
        p.conditionMatching &&
        p.matchingType === -1
      ) {
        indexes.push(i);
      }
    });

  if (!indexes.length) {
    return enums;
  }

  const params = {
    org_id: orgId,
    index: indexes,
    getQuoteArray: true,
  } as any;

  if (nodeId) {
    params.node_id = nodeId;
  }

  if (props.propIndexs) {
    params.temp_id = template.template_id;
    params.ignoreQuotaCondition = true;
  }

  // 获取选项
  const [err, res] = await execCalcFormula(request, params);

  if (res?.status === 'ok' && Array.isArray(res.data)) {
    indexes.forEach((index, i) => {
      if (propIsNull(res.data[i])) return;
      const d = Array.isArray(res.data[i]) ? res.data[i] : [res.data[i]];
      enums[index] = [];
      if (
        (propType && propType === 'address') ||
        (!propType && template.prop[index].type === 'address')
      ) {
        enums[index] = d;
        return;
      }
      d.forEach((d: any) => {
        if (typeof d === 'string' && d && !enums[index].find((x) => x.label === d)) {
          enums[index].push({
            label: d,
            value: d,
          });
        } else if (typeof d === 'object' && d !== null && d.v) {
          const auxiliaryProp = propType ? ap : template.prop[index].auxiliaryProp;
          const aux =
            'aux' in d && auxiliaryProp
              ? {
                  label: Array.isArray(d.aux) ? d.aux.join(';') : d.aux,
                  position: auxiliaryProp?.position,
                }
              : null;
          if (typeof d.v === 'string' && !enums[index].find((x) => x.label === d.v)) {
            enums[index].push({
              label: d.v,
              value: d.v,
              aux: aux,
            });
          } else if (Array.isArray(d.v)) {
            d.v.forEach((v: any) => {
              if (typeof v === 'string' && !enums[index].find((x) => x.label === v)) {
                enums[index].push({
                  label: v,
                  value: v,
                  aux: aux,
                });
              }
            });
          }
        }
      });
    });
  }
  return enums;
};
